import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAppDispatch } from '@core/redux/hooks';
import { useSelector } from 'react-redux';
import { CircularProgress, Stack } from '@mui/material';
import { unwrapResult } from '@reduxjs/toolkit';
import { viewer3dActions, viewer3dSelectors } from '@viewer3D/redux';
import { setEditMode } from '@projects/edit-modes';
import { EditMode } from '@projects/edit-modes/types';
import { setSearchedScanId } from '@projects/pages/detail/menu/ScanOrganization/ScansInScopeList/checkboxSelectionState';
import { scanThunks } from '@scans/redux';
import { errorBox } from '@core/components/AlertBox';

const projectScanPath = (projectId: string, scanId: string) =>
  `/projects/${encodeURIComponent(projectId)}/scans/${encodeURIComponent(scanId)}`;

const getCleanSearch = (search: string) => {
  const params = new URLSearchParams(search);
  let changed = false;
  if (params.has('projectId')) {
    params.delete('projectId');
    changed = true;
  }
  if (params.has('scanId')) {
    params.delete('scanId');
    changed = true;
  }
  if (params.has('scan')) {
    params.delete('scan');
    changed = true;
  }
  if (!changed) return search;
  const str = params.toString();
  return str ? `?${str}` : '';
};

export const useScanUrlRouting = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId, scanId: urlScanId } = useParams<{ projectId?: string; scanId?: string }>();
  const [searchParams] = useSearchParams();

  const queryScanId = searchParams.get('scanId') || searchParams.get('scan');
  const targetScanId = (urlScanId || queryScanId)?.trim();

  const selectedScanId = useSelector(viewer3dSelectors.selectedScanId);
  const selectedScanIdRef = useRef(selectedScanId);
  selectedScanIdRef.current = selectedScanId;

  const prevProjectIdRef = useRef<string | undefined>(projectId);
  const prevSelectedScanIdRef = useRef<string | undefined>(selectedScanId);
  const isInitialMountRef = useRef(true);

  // 1. URL -> State (Initial deep links, URL edits, Browser Back/Forward)
  useEffect(() => {
    if (!projectId) return;

    if (targetScanId) {
      if (selectedScanIdRef.current !== targetScanId) {
        setEditMode(EditMode.Scan);
        setSearchedScanId(targetScanId);
      }
    } else if (selectedScanIdRef.current) {
      // Handles browser back navigation to base project URL
      setSearchedScanId('');
      dispatch(viewer3dActions.setSelectedScanId(undefined));
    }
  }, [projectId, targetScanId, dispatch]);

  // 2. Sync Redux selectedScanId -> URL so current selection is always shareable via canonical path
  useEffect(() => {
    if (!projectId) return;

    if (prevProjectIdRef.current !== projectId) {
      prevProjectIdRef.current = projectId;
      prevSelectedScanIdRef.current = selectedScanId;
      return;
    }

    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      prevSelectedScanIdRef.current = selectedScanId;
      return;
    }

    const prevSelectedScanId = prevSelectedScanIdRef.current;
    prevSelectedScanIdRef.current = selectedScanId;

    if (prevSelectedScanId === selectedScanId) return;

    const cleanSearch = getCleanSearch(location.search);

    if (selectedScanId) {
      const canonicalPath = projectScanPath(projectId, selectedScanId);
      if (location.pathname !== canonicalPath || location.search !== cleanSearch) {
        navigate(
          {
            pathname: canonicalPath,
            search: cleanSearch,
          },
          { replace: true },
        );
      }
    } else {
      // When selection is cleared
      const baseProjectPath = `/projects/${encodeURIComponent(projectId)}`;
      if (urlScanId || queryScanId || location.pathname !== baseProjectPath) {
        navigate(
          {
            pathname: baseProjectPath,
            search: cleanSearch,
          },
          { replace: true },
        );
      }
    }
  }, [
    selectedScanId,
    projectId,
    urlScanId,
    queryScanId,
    location.pathname,
    location.search,
    navigate,
  ]);
};

export const ScanRedirect: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { scanId } = useParams<{ scanId: string }>();
  const [searchParams] = useSearchParams();
  const projectIdParam = searchParams.get('projectId')?.trim();

  useEffect(() => {
    const trimmedScanId = scanId?.trim();
    const cleanSearch = getCleanSearch(location.search);

    if (projectIdParam && trimmedScanId) {
      navigate(
        {
          pathname: projectScanPath(projectIdParam, trimmedScanId),
          search: cleanSearch,
        },
        { replace: true },
      );
      return;
    }

    if (!trimmedScanId) {
      errorBox('Invalid or missing scan ID.');
      navigate('/projects/list', { replace: true });
      return;
    }

    let isMounted = true;

    const resolveProjectAndRedirect = async () => {
      try {
        const action = await dispatch(
          scanThunks.searchWithFilters({
            path: ['scans', 'search', 'withFilters'],
            params: {
              id: trimmedScanId,
              projection: 'projectDetail',
            },
          }),
        );

        if (!isMounted) return;

        const payload = unwrapResult(action);
        const hasResolvedProject = (res: unknown): res is { content: { projectId: string }[] } =>
          typeof res === 'object' &&
          res !== null &&
          'content' in res &&
          Array.isArray((res as { content: unknown }).content) &&
          typeof (res as { content: { projectId?: unknown }[] }).content[0]?.projectId ===
            'string' &&
          Boolean((res as { content: { projectId: string }[] }).content[0].projectId.trim());

        if (hasResolvedProject(payload)) {
          const resolvedProjectId = payload.content[0].projectId.trim();
          navigate(
            {
              pathname: projectScanPath(resolvedProjectId, trimmedScanId),
              search: cleanSearch,
            },
            { replace: true },
          );
        } else {
          errorBox('Scan not found or the link has expired.');
          navigate('/projects/list', { replace: true });
        }
      } catch (err) {
        if (isMounted) {
          console.error('Failed to resolve project for scan:', err);
          errorBox('Failed to resolve scan. The link may be broken or expired.');
          navigate('/projects/list', { replace: true });
        }
      }
    };

    resolveProjectAndRedirect();

    return () => {
      isMounted = false;
    };
  }, [scanId, projectIdParam, dispatch, navigate, location.search]);

  return (
    <Stack alignItems="center" justifyContent="center" height="100vh">
      <CircularProgress />
    </Stack>
  );
};
